import {
  BaseError,
  HttpRequestError,
  RpcError,
  RpcRequestError,
  TimeoutError,
  WebSocketRequestError
} from 'viem'

export interface TransactionErrorSummary {
  message?: string
  name?: string
  code?: string
  details?: string
}

const MAX_CAUSE_DEPTH = 8
const HASH_LENGTH = 66

export function sanitizeTransactionErrorField(
  value: string | undefined,
  maxLength: number
): string | undefined {
  if (typeof value !== 'string') return undefined

  const cleaned = Array.from(value, char => {
    const code = char.codePointAt(0) ?? 0
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) ? ' ' : char
  })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/0x[0-9a-fA-F]{40,}/g, match =>
      match.length === HASH_LENGTH ? match : `${match.slice(0, 6)}…${match.slice(-4)}`
    )

  if (!cleaned) return undefined

  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 3)}...` : cleaned
}

function getProperty(value: unknown, property: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined
  return (value as Record<string, unknown>)[property]
}

export function getRawErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error

  const message = getProperty(error, 'message')
  return typeof message === 'string' ? message : String(error)
}

function getCauseChain(error: unknown): unknown[] {
  const chain: unknown[] = []
  let current = error

  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current !== undefined; depth++) {
    chain.push(current)
    current = getProperty(current, 'cause')
  }

  return chain
}

function getCode(error: unknown): string | undefined {
  const code = getProperty(error, 'code')
  return typeof code === 'number' || typeof code === 'string' ? String(code) : undefined
}

export function summarizeTransactionError(error: unknown): TransactionErrorSummary {
  if (error instanceof BaseError) {
    const chain = getCauseChain(error)
    const name = chain
      .filter((cause): cause is BaseError => cause instanceof BaseError)
      .at(-1)?.name
    const codes = chain.map(getCode).filter((value): value is string => value !== undefined)
    const code = [...codes].reverse().find(value => value !== '-1') ?? codes[0]

    return {
      message: sanitizeTransactionErrorField(error.shortMessage, 200),
      name: name ? sanitizeTransactionErrorField(name, 64) : undefined,
      code: code ? sanitizeTransactionErrorField(code, 32) : undefined,
      details: sanitizeTransactionErrorField(error.details, 200)
    }
  }

  if (error instanceof Error) {
    return {
      message: sanitizeTransactionErrorField(error.message, 200),
      name: sanitizeTransactionErrorField(error.name, 64),
      code: sanitizeTransactionErrorField(getCode(error) ?? '', 32)
    }
  }

  return {
    message: sanitizeTransactionErrorField(getRawErrorMessage(error), 200),
    code: sanitizeTransactionErrorField(getCode(error) ?? '', 32)
  }
}

export function isAppTransportError(error: unknown): boolean {
  return getCauseChain(error).some(
    cause =>
      cause instanceof HttpRequestError ||
      cause instanceof RpcRequestError ||
      cause instanceof WebSocketRequestError ||
      cause instanceof TimeoutError
  )
}

export function isWalletProviderRpcError(error: unknown): boolean {
  return (
    !isAppTransportError(error) && getCauseChain(error).some(cause => cause instanceof RpcError)
  )
}
